import React, { useMemo, useState } from 'react';
import { FieldIssue } from '../../../components/wizard/WizardShell';
import {
    BundleCalPoint,
    BundleThermalCal,
    DeviceBundlePayload,
} from '../../../api/bundle';
import { IssueByPath } from './useDeviceBundleDraft';
import SearchSelect from '../../../components/SearchSelect';

// 设备向导第 ④ 步: 功率标定曲线 (软件功率% → 实测真实功率 W), 整步可跳过.
//
// 标定点决定换算引擎怎么把用户设的百分比换成真实瓦数, 是这台设备最"物理"的一组数据。
// 前端这里只做即时预判 (让人边填边看到问题), 最终判定以服务端 dryRun 为准 ——
// 口径在后端 validateCalibrationPoints 里, 抄一份必然漂移:
//   硬错: 少于 2 个点 / pct 越界 / pct 重复 / realW 为负
//   告警: 非单调递减 (实测噪声, 按原值保留) / 超标准功率 / 缺 0% 或 100% 端点
//
// 热工艺标定 (thermalCal → device_thermal_cal) 2026-09-22 起也挂在这一步, 只对 thermal 框架显示:
// (模式, 材料类型) 唯一, tau/beta 必须 > 0; 激光/刀切框架看不到这块。

interface Props {
    payload: DeviceBundlePayload;
    setPayload: (p: DeviceBundlePayload) => void;
    issueByPath: IssueByPath;
}

/** stdW 标准功率取自第 ① 步的能力字段 (capability.stdW), 用来画纵轴与判超限. */
function stdWOf(payload: DeviceBundlePayload): number {
    const raw = (payload.extValues || {}).capability?.stdW;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function StepCalibration({ payload, setPayload, issueByPath }: Props) {
    // useMemo 稳住引用: `|| []` 每次渲染都是新数组, 直接进 useMemo 依赖会让下面的计算白跑
    const points = useMemo(
        () => payload.calibration || [],
        [payload.calibration],
    );
    const stdW = stdWOf(payload);
    const [bulk, setBulk] = useState('');

    const setPoints = (next: BundleCalPoint[]) =>
        setPayload({ ...payload, calibration: next });

    const sorted = useMemo(
        () => [...points].sort((a, b) => a.pct - b.pct),
        [points],
    );

    // 即时预判 (与服务端同口径, 但服务端说了算)
    const { errors, warnings } = useMemo(() => {
        const errs: string[] = [];
        const warns: string[] = [];
        if (points.length === 0) return { errors: errs, warnings: warns };
        if (points.length < 2) errs.push('标定曲线至少需要 2 个点');
        const seen = new Set<number>();
        sorted.forEach((p, i) => {
            if (p.pct < 0 || p.pct > 100)
                errs.push(`pct=${p.pct} 超出 [0,100]`);
            if (seen.has(p.pct)) errs.push(`pct=${p.pct} 重复`);
            seen.add(p.pct);
            if (p.realW < 0) errs.push(`pct=${p.pct} 的 realW=${p.realW} 为负`);
            if (i > 0 && p.realW < sorted[i - 1].realW) {
                warns.push(
                    `realW 在 pct=${p.pct} 处递减 (${sorted[i - 1].realW} → ${p.realW}), 实测噪声按原值保留`,
                );
            }
            if (stdW > 0 && p.realW > stdW) {
                warns.push(
                    `pct=${p.pct} 的 realW=${p.realW} 超过标准功率 ${stdW}W`,
                );
            }
        });
        if (sorted[0]?.pct !== 0)
            warns.push('缺 0% 端点, 低于最小 pct 的换算按端点截断');
        if (sorted[sorted.length - 1]?.pct !== 100) {
            warns.push('缺 100% 端点, 高于最大 pct 的换算按端点截断');
        }
        return { errors: errs, warnings: warns };
    }, [sorted, points.length, stdW]);

    const addRow = () => setPoints([...points, { pct: 0, realW: 0 }]);

    const patchRow = (i: number, p: Partial<BundleCalPoint>) =>
        setPoints(points.map((row, j) => (j === i ? { ...row, ...p } : row)));

    /** 批量粘贴: 每行 "百分比 功率", 分隔符按空白/逗号/制表符都认 —— 标定数据基本都是从表格里拷出来的. */
    const applyBulk = () => {
        const parsed: BundleCalPoint[] = [];
        bulk.split('\n')
            .map((l) => l.trim())
            .filter(Boolean)
            .forEach((line) => {
                const [a, b] = line.split(/[\s,\t]+/);
                const pct = Number(a);
                const realW = Number(b);
                if (Number.isFinite(pct) && Number.isFinite(realW)) {
                    parsed.push({ pct, realW });
                }
            });
        if (parsed.length) {
            setPoints(parsed);
            setBulk('');
        }
    };

    return (
        <>
            <div className="infobox text-xs">
                标定曲线决定换算引擎怎么把软件功率百分比换成真实瓦数。可以整步跳过,
                创建后用「功率校准」页的导入补录。
            </div>

            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold flex items-center gap-2">
                    标定点 ({points.length})
                    {stdW > 0 && (
                        <span className="text-xs font-normal text-slate-400">
                            标准功率 {stdW} W
                        </span>
                    )}
                    <button
                        type="button"
                        className="ml-auto text-xs text-brand-600 hover:underline font-normal"
                        onClick={addRow}
                    >
                        ＋ 添加一个点
                    </button>
                </div>

                {points.length === 0 ? (
                    <div className="text-xs text-slate-400">还没有标定点。</div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr>
                                <th className="th w-40">软件功率 (%)</th>
                                <th className="th w-40">实测功率 (W)</th>
                                <th className="th" />
                                <th className="th w-14" />
                            </tr>
                        </thead>
                        <tbody>
                            {points.map((p, i) => (
                                <tr key={i}>
                                    <td className="td">
                                        <input
                                            className="inp font-mono"
                                            type="number"
                                            step="any"
                                            value={p.pct}
                                            onChange={(e) =>
                                                patchRow(i, {
                                                    pct: Number(e.target.value),
                                                })
                                            }
                                        />
                                    </td>
                                    <td className="td">
                                        <input
                                            className="inp font-mono"
                                            type="number"
                                            step="any"
                                            value={p.realW}
                                            onChange={(e) =>
                                                patchRow(i, {
                                                    realW: Number(
                                                        e.target.value,
                                                    ),
                                                })
                                            }
                                        />
                                    </td>
                                    <td className="td" />
                                    <td className="td">
                                        <button
                                            type="button"
                                            className="btn-danger-text"
                                            onClick={() =>
                                                setPoints(
                                                    points.filter(
                                                        (_, j) => j !== i,
                                                    ),
                                                )
                                            }
                                        >
                                            移除
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                <details className="text-xs">
                    <summary className="cursor-pointer text-brand-600">
                        从表格批量粘贴
                    </summary>
                    <div className="mt-2 space-y-2">
                        <textarea
                            className="inp w-full h-24 resize-none font-mono text-xs"
                            value={bulk}
                            onChange={(e) => setBulk(e.target.value)}
                            placeholder={
                                '每行一个点, 百分比与功率用空格/逗号分开:\n0 0\n10 1.9\n100 19.8'
                            }
                        />
                        <button
                            type="button"
                            className="btn-ghost"
                            onClick={applyBulk}
                            disabled={!bulk.trim()}
                        >
                            解析并替换现有点
                        </button>
                    </div>
                </details>
                <FieldIssue issue={issueByPath['calibration']} />
            </div>

            {sorted.length >= 2 && (
                <div className="card p-4 space-y-2">
                    <div className="text-sm font-semibold">曲线预览</div>
                    <CalibChart points={sorted} stdW={stdW} />
                </div>
            )}

            {payload.device.archCode === 'thermal' && (
                <ThermalCalCard
                    payload={payload}
                    setPayload={setPayload}
                    issueByPath={issueByPath}
                />
            )}

            {(errors.length > 0 || warnings.length > 0) && (
                <div className="card p-4 space-y-1.5">
                    <div className="text-sm font-semibold">即时检查</div>
                    {errors.map((e) => (
                        <div key={e} className="text-xs text-rose-600">
                            ✖ {e}
                        </div>
                    ))}
                    {warnings.map((w) => (
                        <div key={w} className="text-xs text-amber-600">
                            ⚠ {w}
                        </div>
                    ))}
                    <div className="text-[11px] text-slate-400 pt-1">
                        以提交前的服务端预检为准, 这里只是边填边看。
                    </div>
                </div>
            )}
        </>
    );
}

/** ThermalCalCard 热工艺标定 (thermal 框架): 每行 模式 × 材料类型 → tau / beta. */
function ThermalCalCard({ payload, setPayload, issueByPath }: Props) {
    const rows = useMemo(() => payload.thermalCal || [], [payload.thermalCal]);
    const setRows = (next: BundleThermalCal[]) =>
        setPayload({ ...payload, thermalCal: next });
    const patchRow = (i: number, p: Partial<BundleThermalCal>) =>
        setRows(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
    const numOrNull = (raw: string) => (raw === '' ? null : Number(raw));
    // 模式候选 = 第 ② 步已勾的模式引用 (可手填其它值, 服务端只查非空与重复)
    const modeOptions = (payload.modeRefs || []).map((r) => r.modeValue);

    return (
        <div className="card p-4 space-y-3">
            <div className="text-sm font-semibold flex items-center gap-2">
                热工艺标定 ({rows.length})
                <span className="text-xs font-normal text-slate-400">
                    device_thermal_cal · 仅热工艺框架
                </span>
                <button
                    type="button"
                    className="ml-auto text-xs text-brand-600 hover:underline font-normal"
                    onClick={() =>
                        setRows([
                            ...rows,
                            { mode: modeOptions[0] || '', materialType: '' },
                        ])
                    }
                >
                    ＋ 添加一行
                </button>
            </div>
            {rows.length === 0 ? (
                <div className="text-xs text-slate-400">
                    还没有标定行。(模式, 材料类型) 唯一, tau / beta 须大于 0。
                </div>
            ) : (
                <table className="w-full">
                    <thead>
                        <tr>
                            <th className="th">模式</th>
                            <th className="th">材料类型</th>
                            <th className="th w-32">tau</th>
                            <th className="th w-32">beta</th>
                            <th className="th w-14" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td className="td">
                                    <SearchSelect
                                        className="w-full"
                                        value={r.mode}
                                        onChange={(v) => patchRow(i, { mode: v })}
                                        options={modeOptions.map((m) => ({ value: m, label: m }))}
                                        placeholder="如 hotpress (可搜索, 或输入新模式)"
                                        creatable
                                    />
                                </td>
                                <td className="td">
                                    <input
                                        className="inp text-xs"
                                        value={r.materialType}
                                        onChange={(e) =>
                                            patchRow(i, {
                                                materialType: e.target.value,
                                            })
                                        }
                                        placeholder="材料类型"
                                    />
                                </td>
                                <td className="td">
                                    <input
                                        className="inp font-mono text-xs"
                                        type="number"
                                        step="any"
                                        value={r.tau ?? ''}
                                        onChange={(e) =>
                                            patchRow(i, {
                                                tau: numOrNull(e.target.value),
                                            })
                                        }
                                    />
                                </td>
                                <td className="td">
                                    <input
                                        className="inp font-mono text-xs"
                                        type="number"
                                        step="any"
                                        value={r.beta ?? ''}
                                        onChange={(e) =>
                                            patchRow(i, {
                                                beta: numOrNull(e.target.value),
                                            })
                                        }
                                    />
                                </td>
                                <td className="td">
                                    <button
                                        type="button"
                                        className="btn-danger-text"
                                        onClick={() =>
                                            setRows(
                                                rows.filter((_, j) => j !== i),
                                            )
                                        }
                                    >
                                        移除
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <FieldIssue issue={issueByPath['thermalCal']} />
        </div>
    );
}

/** CalibChart 标定曲线 (手写 SVG, 与工况页/校准页同一画法, 项目内无图表库). */
function CalibChart({
    points,
    stdW,
}: {
    points: BundleCalPoint[];
    stdW: number;
}) {
    const W = 560;
    const H = 160;
    const pad = 28;
    const top = Math.max(stdW || 1, ...points.map((p) => p.realW));
    const x = (pct: number) => pad + (pct / 100) * (W - 2 * pad);
    const y = (w: number) => H - pad - (w / top) * (H - 2 * pad);
    const path = points
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.pct)} ${y(p.realW)}`)
        .join(' ');
    return (
        <svg width={W} height={H} className="block max-w-full">
            {[0, 25, 50, 75, 100].map((g) => (
                <line
                    key={g}
                    x1={x(g)}
                    y1={pad}
                    x2={x(g)}
                    y2={H - pad}
                    stroke="#f1f5f9"
                />
            ))}
            <line
                x1={pad}
                y1={H - pad}
                x2={W - pad}
                y2={H - pad}
                stroke="#cbd5e1"
            />
            <line x1={pad} y1={pad} x2={pad} y2={H - pad} stroke="#cbd5e1" />
            <path d={path} fill="none" stroke="#3b6cf6" strokeWidth={2} />
            {points.map((p) => (
                <circle
                    key={p.pct}
                    cx={x(p.pct)}
                    cy={y(p.realW)}
                    r={2.5}
                    fill="#3b6cf6"
                />
            ))}
            {[0, 25, 50, 75, 100].map((g) => (
                <text
                    key={g}
                    x={x(g)}
                    y={H - 10}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#94a3b8"
                >
                    {g}%
                </text>
            ))}
            <text
                x={W - pad}
                y={H - pad - 4}
                textAnchor="end"
                fontSize="10"
                fill="#94a3b8"
            >
                软件功率%
            </text>
            <text x={pad + 4} y={pad + 10} fontSize="10" fill="#94a3b8">
                真实功率 W
            </text>
        </svg>
    );
}

export default StepCalibration;
